/* ============================================================
   ProductOptionsEditor — a1a3b8f4 seller-defined priced options
   editor (boolean / choice / quantity × fixed / per-unit).
   Extracted from the retired inline ProviderProductsPanel form for
   the separate product edit page (0e895f9a). Controlled component:
   pure onChange, no internal state.
   ============================================================ */

import type { ProductOptionDraft } from '../types/product';

/** Matches backend ProductOptionRules limits. */
const MAX_OPTIONS = 12;
const MAX_VALUES = 8;

const EMPTY_OPTION: ProductOptionDraft = {
  name: '',
  type: 0,
  priceMode: 0,
  priceRub: 0,
  enabled: true,
  values: null,
};

const fieldStyle: React.CSSProperties = {
  height: 44,
  padding: '0 var(--space-md)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
  fontSize: 'var(--fs-body)',
  width: '100%',
};

interface Props {
  options: ProductOptionDraft[];
  onChange: (next: ProductOptionDraft[]) => void;
}

export function ProductOptionsEditor({ options, onChange }: Props) {
  const setOption = (oi: number, patch: Partial<ProductOptionDraft>) =>
    onChange(options.map((o, i) => (i === oi ? { ...o, ...patch } : o)));

  return (
    <div style={{ display: 'grid', gap: 'var(--space-xs)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-sm)' }}>
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          Платные опции · до {MAX_OPTIONS}
        </span>
        <button
          className="btn btn-secondary"
          style={{ height: 32, padding: '0 var(--space-sm)', fontSize: 'var(--fs-caption)' }}
          disabled={options.length >= MAX_OPTIONS}
          onClick={() => onChange([...options, { ...EMPTY_OPTION, values: [] }])}
        >
          + Опция
        </button>
      </div>
      {options.map((option, oi) => (
        <div
          key={oi}
          style={{
            border: '1px solid var(--color-separator)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-sm)',
            display: 'grid',
            gap: 'var(--space-xs)',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 'var(--space-xs)', alignItems: 'center' }}>
            <input style={fieldStyle} placeholder="Название (напр. Покраска)" value={option.name}
                   onChange={(e) => setOption(oi, { name: e.target.value })} />
            <select
              style={fieldStyle}
              value={option.type}
              onChange={(e) => {
                const type = Number(e.target.value);
                setOption(oi, {
                  type,
                  values: type === 1 ? option.values ?? [] : null,
                  priceMode: type === 2 ? 1 : option.priceMode,
                });
              }}
            >
              <option value={0}>Да/Нет</option>
              <option value={1}>Выбор</option>
              <option value={2}>Количество</option>
            </select>
            {option.type === 2 ? (
              <input style={fieldStyle} type="number" min={0} placeholder="₽/шт." value={option.priceRub || ''}
                     onChange={(e) => setOption(oi, { priceRub: Number(e.target.value) })} />
            ) : (
              <select style={fieldStyle} value={option.priceMode} disabled={option.type === 2}
                      onChange={(e) => setOption(oi, { priceMode: Number(e.target.value) })}>
                <option value={0}>+ раз за строку</option>
                <option value={1}>+ за штуку</option>
              </select>
            )}
            <button
              aria-label="Удалить опцию"
              style={{ color: 'var(--color-danger)', cursor: 'pointer' }}
              onClick={() => onChange(options.filter((_, i) => i !== oi))}
            >
              ×
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            {option.type === 0 && (
              <input style={{ ...fieldStyle, width: 140 }} type="number" min={0} placeholder="Цена, ₽" value={option.priceRub || ''}
                     onChange={(e) => setOption(oi, { priceRub: Number(e.target.value) })} />
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-caption)' }}>
              <input type="checkbox" checked={option.enabled} onChange={(e) => setOption(oi, { enabled: e.target.checked })} />
              Активна
            </label>
          </div>
          {option.type === 1 && (
            <div style={{ display: 'grid', gap: 'var(--space-xs)' }}>
              {(option.values ?? []).map((value, vi) => (
                <div key={vi} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 'var(--space-xs)', alignItems: 'center' }}>
                  <input
                    style={fieldStyle}
                    placeholder="Вариант (напр. Белый)"
                    value={value.label}
                    onChange={(e) =>
                      setOption(oi, {
                        values: (option.values ?? []).map((v, i) => (i === vi ? { ...v, label: e.target.value } : v)),
                      })
                    }
                  />
                  <input
                    style={fieldStyle}
                    type="number"
                    min={0}
                    placeholder="+, ₽"
                    value={value.priceRub || ''}
                    onChange={(e) =>
                      setOption(oi, {
                        values: (option.values ?? []).map((v, i) => (i === vi ? { ...v, priceRub: Number(e.target.value) } : v)),
                      })
                    }
                  />
                  <button
                    aria-label="Удалить вариант"
                    style={{ color: 'var(--color-danger)', cursor: 'pointer' }}
                    onClick={() => setOption(oi, { values: (option.values ?? []).filter((_, i) => i !== vi) })}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                className="btn btn-secondary"
                style={{ height: 30, padding: '0 var(--space-sm)', fontSize: 'var(--fs-caption)', justifySelf: 'start' }}
                disabled={(option.values?.length ?? 0) >= MAX_VALUES}
                onClick={() => setOption(oi, { values: [...(option.values ?? []), { label: '', priceRub: 0 }] })}
              >
                + Вариант
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
